import { Column, Columns, FlexBox, Spinner } from '@dixa/lib-shared-habla';
import { SearchResultEnriched } from 'lib-search-types';
import { useEffect } from 'react';

import {
  ConversationContextProvider,
  ConversationSummaryByCsid,
} from 'components/conversation';
import { actions as conversationActions } from 'components/conversation/actions/conversation';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';

import { ConversationPreviewHeader } from './components/header/conversationPreviewHeader';
import { ConversationPreviewMessages } from './components/messages/conversationPreviewMessages';

type ConversationPreviewProps = {
  csid: number;
  highlights: SearchResultEnriched<'conversations'>['highlights'];
  onGoToConversation: () => void;
  onPreviewClose: () => void;
};

export const ConversationPreview = ({
  csid,
  highlights,
  onGoToConversation,
  onPreviewClose,
}: ConversationPreviewProps): JSX.Element => {
  const dispatch = useAppDispatch();

  const { metaConversation, asyncStatus } = useAppSelector((state) => ({
    metaConversation: state.conversation.metaConversation,
    asyncStatus: state.conversation.asyncStatus.getConversation,
  }));

  const conversation = metaConversation.conversation;

  // We continue to use the legacy API here due to the fact that the component tree rendered below
  // relies on some legacy components to display conversation information. Until we'll have time to
  // completely overhaul the UI and rewrite components, we won't be using the new RTK Query APIs.
  useEffect(() => {
    dispatch(
      conversationActions.conversationGet({
        csid,
        skipStoreAndWs: true,
      }),
    );
  }, [dispatch, csid]);

  const isLoading = asyncStatus === 'loading';

  return (
    <Columns>
      <Column>
        <Column.Row noMinHeight noHorizontalPadding noVerticalPadding>
          <ConversationPreviewHeader
            csid={csid}
            metaConversation={metaConversation}
            highlights={highlights}
            onPreviewClose={onPreviewClose}
            onGoToConversation={onGoToConversation}
          />
        </Column.Row>

        <Column.Row size={1} scrollable>
          {isLoading ? (
            <FlexBox
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              height="100%"
            >
              <Spinner size={18} />
            </FlexBox>
          ) : (
            <FlexBox flexDirection="column" gap="200">
              {conversation.id && conversation.summary?.can_be_generated && (
                <ConversationContextProvider conversation={conversation}>
                  <ConversationSummaryByCsid
                    csid={conversation.id}
                    view="searchView"
                  />
                </ConversationContextProvider>
              )}

              <ConversationPreviewMessages
                messages={metaConversation.messages}
                requesterId={conversation.requested_by}
              />
            </FlexBox>
          )}
        </Column.Row>
      </Column>
    </Columns>
  );
};
